import React from 'react';
import { Modal, Button, Card, Badge, Spinner, Alert } from 'react-bootstrap';

export interface TemplatePreviewStepConfig {
  decisions?: string[];
  decisionNoteRequired?: boolean;
  decisionNoteEnabled?: boolean;
  type?: string;
  reports?: string[];
  allowRegenerate?: boolean;
  sharingMode?: string;
  [key: string]: unknown;
}

export interface TemplatePreviewData {
  templateName: string;
  templateCategory: string;
  templateDescription?: string;
  totalSteps: number;
  requiredSteps: number;
  steps: Array<{
    stepName: string;
    stepDescription: string;
    isRequired: boolean;
    isActive: boolean;
    stepConfig?: TemplatePreviewStepConfig;
  }>;
  templateGuid?: string;
  isLoadingSteps?: boolean;
}

interface TemplatePreviewModalProps {
  show: boolean;
  onHide: () => void;
  template: TemplatePreviewData | null;
  /** When true, shows a link at the bottom to open the full editor. Default false. */
  showEditorLink?: boolean;
}

const StepConfigDetail: React.FC<{ config: TemplatePreviewStepConfig }> = ({ config }) => {
  const isReportTrigger = String(config.type ?? '').toLowerCase() === 'reporttrigger';

  if (isReportTrigger) {
    const reports = Array.isArray(config.reports) ? config.reports : [];
    return (
      <div className="mt-2 pt-2 border-top">
        <div className="small fw-semibold text-muted mb-1">Report Trigger</div>
        {reports.length > 0 ? (
          <div className="d-flex flex-wrap gap-1">
            {reports.map((r) => (
              <Badge key={r} bg="info" text="dark" className="fw-normal">{r}</Badge>
            ))}
          </div>
        ) : (
          <span className="small text-muted">No reports configured</span>
        )}
        <div className="small text-muted mt-1">
          {config.allowRegenerate ? 'Regeneration allowed' : 'No regeneration'}
          {config.sharingMode ? ` · Sharing: ${config.sharingMode}` : ''}
        </div>
      </div>
    );
  }

  const decisions = Array.isArray(config.decisions) ? config.decisions : [];
  if (decisions.length > 0) {
    return (
      <div className="mt-2 pt-2 border-top">
        <div className="small fw-semibold text-muted mb-1">Decision Options</div>
        <div className="d-flex flex-column gap-1">
          {decisions.map((d) => (
            <div
              key={d}
              className="d-flex align-items-center gap-2 px-2 py-1 rounded"
              style={{ border: '1px solid var(--bs-border-color)', fontSize: '0.85rem' }}
            >
              <span
                className="rounded-circle d-inline-block flex-shrink-0"
                style={{ width: 10, height: 10, border: '2px solid var(--bs-secondary-color)' }}
              />
              {d}
            </div>
          ))}
        </div>
        {(config.decisionNoteRequired || config.decisionNoteEnabled) && (
          <div className="small text-muted mt-1">
            {config.decisionNoteRequired ? 'Note required' : 'Note optional'}
          </div>
        )}
      </div>
    );
  }

  return null;
};

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  show,
  onHide,
  template,
  showEditorLink = false,
}) => {
  if (!template) return null;

  const handleEditClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onHide();
    if (template.templateGuid) {
      window.location.href = `/admin/reviews/templates/${template.templateGuid}/edit?preview=true`;
    }
  };


  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable centered>
      <Modal.Header closeButton>
        <Modal.Title>Template Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <h5 className="mb-1">{template.templateName}</h5>
          <div className="mb-2">
            <Badge bg="secondary">{template.templateCategory}</Badge>
          </div>
          {template.templateDescription && (
            <p className="text-muted mb-0">{template.templateDescription}</p>
          )}
        </div>

        <div className="mb-3">
          <strong>Steps</strong>
          <div className="small text-muted mb-2">
            {template.requiredSteps} required of {template.totalSteps} total
          </div>
        </div>

        {template.isLoadingSteps ? (
          <div className="text-center p-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading step details...
          </div>
        ) : template.steps.length === 0 ? (
          <Alert variant="info">No steps defined.</Alert>
        ) : (
          <div className="d-flex flex-column gap-2">
            {template.steps.map((step, index) => (
              <Card key={index} className="border-light-subtle">
                <Card.Body className="p-3">
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                    <strong>
                      Step {index + 1}: {step.stepName || '(Untitled)'}
                    </strong>
                    <div className="d-flex gap-1 flex-shrink-0">
                      {step.isRequired && (
                        <Badge bg="warning" text="dark">Required</Badge>
                      )}
                      {!step.isActive && (
                        <Badge bg="secondary">Inactive</Badge>
                      )}
                    </div>
                  </div>
                  {step.stepDescription && (
                    <p className="text-muted small mb-0">{step.stepDescription}</p>
                  )}
                  {step.stepConfig && (
                    <StepConfigDetail config={step.stepConfig} />
                  )}
                </Card.Body>
              </Card>
            ))}
          </div>
        )}

        {showEditorLink && template.templateGuid && template.steps.length > 0 && (
          <Alert variant="info" className="mt-3 mb-0">
            <small>
              For a detailed preview showing how configuration affects step display,{' '}
              <a
                href={`/admin/reviews/templates/${template.templateGuid}/edit?preview=true`}
                onClick={handleEditClick}
              >
                open the template editor
              </a>.
            </small>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TemplatePreviewModal;
